/**
 * 全局错误处理
 * - Vue app.config.errorHandler — 捕获组件内未处理错误
 * - window.onunhandledrejection — 捕获 Promise 错误
 *
 * 错误通过 logger.error 记录，生产环境由 logger 批量上报给 Rust
 * 用户可见的错误通过 Toast 友好提示，不直接暴露系统错误
 */
import type { App } from 'vue'
import * as Sentry from '@sentry/vue'
import { logger } from '@/utils/logger'
import { TokenExpiredError, BusinessError, NetworkError } from '@/api/client'
import { shouldDropSentryEvent } from '@/utils/sentry'
import { isChunkLoadError } from '@/utils/versionCheck'

/** 错误上报节流：相同 key 在 5s 内不重复上报 */
const errorThrottle = new Map<string, number>()
const ERROR_THROTTLE_MS = 5_000
const MAX_REASON_KEYS = 8

function safeString(value: unknown): string {
  if (value === null || value === undefined) return ''
  try {
    return String(value)
  } catch {
    return ''
  }
}

function getReasonKeys(value: unknown): string[] {
  if ((typeof value !== 'object' && typeof value !== 'function') || value === null) return []
  try {
    return Object.keys(value).slice(0, MAX_REASON_KEYS)
  } catch {
    return []
  }
}

function getReasonType(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'array'
  const valueType = typeof value
  if (valueType === 'function') return 'function'
  return valueType
}

function getReasonMessage(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) {
    const message = safeString(value.message).trim()
    return message || value.name || 'Error'
  }
  const reasonType = getReasonType(value)
  const keys = getReasonKeys(value)
  return keys.length > 0
    ? `Non-Error rejection (${reasonType}; keys: ${keys.join(',')})`
    : `Non-Error rejection (${reasonType})`
}

function normalizeError(value: unknown, fallbackMessage?: string): Error {
  if (value instanceof Error) {
    const message = fallbackMessage?.trim() || getReasonMessage(value)
    const normalized = new Error(message || 'Error')
    normalized.name = safeString(value.name).trim() || 'Error'
    normalized.stack = value.stack
    return normalized
  }
  const message = fallbackMessage?.trim() || getReasonMessage(value)
  return new Error(message || 'Unknown error')
}

function getReasonMetadata(value: unknown): Record<string, unknown> | undefined {
  if (value instanceof Error) return undefined
  return {
    reasonType: getReasonType(value),
    reasonKeys: getReasonKeys(value),
  }
}

function shouldReport(key: string): boolean {
  const now = Date.now()
  const last = errorThrottle.get(key)
  if (last && now - last < ERROR_THROTTLE_MS) return false
  errorThrottle.set(key, now)
  if (errorThrottle.size > 100) {
    for (const [k, v] of errorThrottle) {
      if (v < now - ERROR_THROTTLE_MS) errorThrottle.delete(k)
    }
  }
  return true
}

function shouldIgnoreError(value: unknown, fallbackMessage?: string): boolean {
  const error = normalizeError(value, fallbackMessage)
  return shouldDropSentryEvent(
    {
      message: error.message,
      exception: {
        values: [{ type: error.name, value: error.message }],
      },
    },
    { originalException: value },
  )
}

/** Vue 组件错误处理 */
function handleVueError(err: unknown, instance: unknown, info: string) {
  const error = normalizeError(err)
  if (shouldIgnoreError(error)) return
  const componentName = instance
    ? (instance as { $options?: { name?: string } }).$options?.name
    : 'unknown'
  const key = `vue:${componentName}:${error.message}`
  if (!shouldReport(key)) return
  logger.error('Vue', `[${info}] ${error.message}`, {
    stack: error.stack,
    component: componentName,
  })
  Sentry.captureException(error, {
    tags: { errorSource: 'vue' },
    extra: { info, component: componentName },
  })
}

/** Promise 未处理拒绝 */
function handleUnhandledRejection(event: PromiseRejectionEvent) {
  const reason = event.reason
  // Token 过期和业务错误已有专门处理，不需要再上报
  if (reason instanceof TokenExpiredError) return
  if (reason instanceof BusinessError) return
  if (reason instanceof NetworkError) return
  if (isChunkLoadError(reason)) return
  const error = normalizeError(reason)
  if (shouldIgnoreError(reason, error.message)) return

  if (!shouldReport(`promise:${error.message}`)) return
  const reasonMetadata = getReasonMetadata(reason)
  logger.error('Promise', `Unhandled rejection: ${error.message}`, {
    stack: error.stack,
    ...(reasonMetadata ?? {}),
  })
  Sentry.captureException(error, {
    tags: { errorSource: 'unhandledrejection' },
    extra: reasonMetadata,
  })
}

/** 全局脚本错误（资源加载失败、语法错误等） */
function handleGlobalError(event: ErrorEvent) {
  if (isChunkLoadError(event.error ?? event.message)) return
  if (shouldIgnoreError(event.error, event.message)) return
  if (!shouldReport(`script:${event.filename}:${event.lineno}`)) return
  logger.error('script', event.message, {
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
  })
  Sentry.captureException(event.error ?? new Error(event.message), {
    tags: { errorSource: 'script' },
    extra: {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    },
  })
}

/** 注册全局错误处理（在 main.ts 中调用） */
export function setupErrorHandler(app: App) {
  app.config.errorHandler = handleVueError

  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  window.addEventListener('error', handleGlobalError)
}
